import { BoardCardMenu } from "@/components/board/BoardCardMenu";
import { Icon } from "@/components/ui/Icon";
import { RauteMark } from "@/components/ui/RauteMark";
import { useResolvedWorkspaceColor } from "@/hooks/useResolvedWorkspaceColor";
import { usePersonalWorkspaceId } from "@/hooks/useWorkspaceMeta";
import { t } from "@/lib/strings";
import type { Board } from "@/lib/types";

function accessChip(access: Board["access"]): string | null {
  if (access === "guest") return t.guestBadge;
  if (access === "shared") return t.sharedBadge;
  return null;
}

/**
 * A board as a compact list row (replaces the card grid on Home + WorkspacePage).
 * Colour dot from the board's workspace; guests/shared show a chip, member boards
 * show the management menu.
 */
export function BoardListRow({ board, onClick }: { board: Board; onClick: () => void }) {
  const color = useResolvedWorkspaceColor(board.workspace_id);
  const personalWsId = usePersonalWorkspaceId();
  const isPersonal = board.workspace_id === personalWsId;
  const chip = accessChip(board.access);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 12px",
        borderRadius: 8,
        cursor: "pointer",
        border: "1px solid var(--hairline)",
        background: "var(--surface)",
        transition: "background .12s, border-color .12s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface)")}
    >
      <RauteMark color={color} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13.5,
          fontWeight: 500,
          color: "var(--ink-1)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {board.name}
      </span>
      {isPersonal ? (
        <span
          title={t.personalBoardHint}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 10,
            fontWeight: 600,
            color: "var(--ink-3)",
            background: "var(--surface-soft)",
            border: "1px solid var(--hairline)",
            borderRadius: 5,
            padding: "2px 6px",
            flexShrink: 0,
          }}
        >
          <Icon name="user-round-key" size={11} color="var(--ink-3)" />
          {t.personalBadge}
        </span>
      ) : null}
      {board.archived_at ? (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "var(--ink-4)",
            background: "var(--surface-soft)",
            border: "1px solid var(--hairline)",
            borderRadius: 5,
            padding: "2px 6px",
            flexShrink: 0,
          }}
        >
          {t.filtersArchived}
        </span>
      ) : null}
      {chip ? (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "var(--ink-3)",
            background: "var(--surface-soft)",
            border: "1px solid var(--hairline)",
            borderRadius: 5,
            padding: "2px 6px",
            flexShrink: 0,
          }}
        >
          {chip}
        </span>
      ) : (
        <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <BoardCardMenu
            boardId={board.id}
            workspaceId={board.workspace_id}
            boardName={board.name}
            boardDescription={board.description ?? ""}
            kind={board.kind}
            archived={!!board.archived_at}
            homeListed={board.home_listed}
          />
        </span>
      )}
    </div>
  );
}
